// /inventory -- STOCK ON HAND, READ FROM THE REAL SOURCE.
//
// This was the last screen reading mocks/inventory.ts: 140 rows generated from
// mocks/products.ts, each with an invented unit cost and an invented stock
// value. It is now catalog identity from /anixify/api/products joined to
// reported quantities from /anixify/api/stock, keyed on product id.
//
// WHY CATALOG-DRIVEN RATHER THAN STOCK-DRIVEN. The page fetches a page of
// products and then asks the stock endpoint about those ids -- the picker's
// pattern, second use. Three reasons, and the third is the important one:
// there is no new endpoint mode to add; the product names come free with the
// same read; and it makes NOT REPORTED VISIBLE. A stock-driven page would list
// only what a source has spoken about, quietly hiding the fact that this
// system currently knows nothing about almost every product it sells.
//
// The stated cost: a product reported on but outside the fetched page is not
// shown. That is the same contract /catalog already carries, and the count
// line says so when the limit actually bites.
//
// TWO READS, TWO FAILURE MODES -- see the banners below. The catalog fails
// LOUD (no products, no screen); stock fails SOFT but prominent (the rows are
// real catalog facts and stay, every quantity reads unreported). Blanking the
// page on a stock failure would be the absent-is-not-zero bug at PAGE SCALE.
//
// ⚠️ NO COST, NO VALUE, NO ALLOCATION DATA, EVER. See InventoryTable.tsx's
// header and addons/anixify_cpq/models/stock.py. A grep test pins it.

import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { searchProducts, getStock, NO_STOCK_DATA_TEXT } from "@/services/api";
import { InventoryTable } from "@/components/InventoryTable";
import { InventoryDetailPanel } from "@/components/InventoryDetailPanel";
import { buildInventoryRows, summariseInventory } from "@/services/inventoryRows";
import type { InventoryRow } from "@/services/inventoryRows";
import type { CatalogProduct } from "@/types";
import type { StockLocationLevel } from "@/services/api";
import { colors, semanticColors, radius, spacing, heights, layout, typography, iconSize, iconStroke, shadows } from "@/styles/tokens";

// Same page size and same reasoning as /catalog: the endpoint defaults to 50
// and caps at 500, and 200 is a page a person can scroll.
const CATALOG_LIMIT = 200;

const SELECT_STYLE: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`, fontSize: typography.fontSize.base,
  color: semanticColors.textPrimary, backgroundColor: semanticColors.surface,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  outline: "none",
};

function StatBlock({
  value, label, testid, color,
}: { value: React.ReactNode; label: string; testid: string; color?: string }) {
  return (
    <div className="flex flex-col" data-testid={testid}>
      <div style={{
        fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold,
        color: color ?? semanticColors.textPrimary, fontVariantNumeric: "tabular-nums",
      }}>
        {value}
      </div>
      <div style={{
        fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
        textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide, marginTop: spacing[2],
      }}>
        {label}
      </div>
    </div>
  );
}

export default function InventoryPage() {
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [catalogStatus, setCatalogStatus] = useState<"loading" | "ok" | "error">("loading");
  const [catalogError, setCatalogError] = useState("");
  const [catalogRetry, setCatalogRetry] = useState(0);

  // ⚠️ THREE STATES, NOT TWO. `null` means the read failed or has not
  // happened; an empty Map means it SUCCEEDED and the provider reported
  // nothing. Those are different facts and the screen says different things
  // about them -- an empty Map rendered as a failure would blame the network
  // for an honest silence, and a failure rendered as an empty Map would claim
  // every product was checked and found empty.
  const [stock, setStock] = useState<Map<number, number> | null>(null);
  const [locations, setLocations] = useState<Map<number, StockLocationLevel[]> | undefined>(undefined);
  const [stockError, setStockError] = useState("");
  const [stockRetry, setStockRetry] = useState(0);
  // ⚠️ "loading" IS ITS OWN STATE, not a shade of unreported. Before the read
  // settles, "nobody has reported on this product" has not been established,
  // and rendering it early is a claim ahead of the evidence -- the same class
  // of error as rendering 0, only quieter.
  const [stockStatus, setStockStatus] = useState<"loading" | "ok" | "error">("loading");
  // no-mock-stock-on-live: the backend has no real stock source connected.
  const [noStockSource, setNoStockSource] = useState(false);
  // ⚠️ WHICH PRODUCTS THE HELD STOCK MAP ACTUALLY ANSWERS FOR.
  //
  // Without this there is a window -- after a catalog page lands, before the
  // stock effect runs -- where NEW rows are rendered against the PREVIOUS
  // read's map. Every one of them resolves to "not reported", which is a
  // claim, made about products nobody has asked about yet. The same window
  // reopens on every search that changes the result set.
  //
  // Same fingerprint discipline the quote screen's staleness guard uses: hold
  // what the answer was for, and treat a mismatch as "still reading" rather
  // than as an answer.
  const [stockFor, setStockFor] = useState<string>("");

  const [search, setSearch]     = useState("");
  const [location, setLocation] = useState<string>("All");
  const [brand, setBrand]       = useState<string>("All");
  const [category, setCategory] = useState<string>("All");
  const [selected, setSelected] = useState<InventoryRow | null>(null);

  // ---- the catalog read ----------------------------------------------------
  //
  // Debounced and last-response-wins, exactly as /catalog and the picker do:
  // two searches in flight can land out of order, and the slower earlier one
  // would otherwise overwrite the newer rows. A stale answer is dropped whole,
  // error included.
  const fetchToken = useRef(0);
  useEffect(() => {
    const token = ++fetchToken.current;
    const query = search.trim();
    setCatalogStatus("loading");

    const timer = setTimeout(async () => {
      const result = await searchProducts(query, CATALOG_LIMIT);
      if (fetchToken.current !== token) return;

      if (!result.ok || !result.products) {
        // CLEARED, not left standing. Stale rows under an error message read
        // as current results.
        setProducts([]);
        setCatalogError(result.error ?? "The product catalog could not be loaded.");
        setCatalogStatus("error");
        return;
      }
      setProducts(result.products);
      setCatalogError("");
      setCatalogStatus("ok");
    }, 250);

    return () => clearTimeout(timer);
  }, [search, catalogRetry]);

  // ---- the stock read ------------------------------------------------------
  //
  // ⚠️ locations: true. This screen shows one row per PLACE, not one figure per
  // product, so it needs the breakdown -- unlike the picker, which shows a
  // single number and deliberately does not ask, because naming one of several
  // warehouses beside a total would attribute all of it to part of it.
  const idsKey = useMemo(() => products.map((p) => p.id).join(","), [products]);

  const stockToken = useRef(0);
  useEffect(() => {
    const token = ++stockToken.current;
    const ids = products.map((p) => p.id);

    if (ids.length === 0) {
      // Nothing to ask about. getStock guards this too (an empty list is NOT a
      // request for the whole catalog), but not firing is clearer.
      setStock(new Map());
      setLocations(new Map());
      setStockError("");
      setStockStatus("ok");
      setStockFor("");
      return;
    }

    setStockStatus("loading");
    let cancelled = false;
    (async () => {
      const result = await getStock(ids, { locations: true });
      if (cancelled || stockToken.current !== token) return;

      if (!result.ok || !result.stock) {
        // ⚠️ UNKNOWN, NOT EMPTY. `null` rather than `new Map()`: an empty map
        // would say every product was checked and found to have none, which is
        // a claim nobody made.
        setStock(null);
        setLocations(undefined);
        setStockError(result.error ?? "Stock levels could not be read.");
        setStockStatus("error");
        // A failure IS an answer for these ids: the rows must settle on
        // "not reported" rather than spinning forever.
        setStockFor(idsKey);
        return;
      }
      setStock(result.stock);
      setLocations(result.locations);
      setNoStockSource(result.configured === false);
      setStockError("");
      setStockStatus("ok");
      setStockFor(idsKey);
    })();

    return () => { cancelled = true; };
  }, [products, idsKey, stockRetry]);

  // "Still reading" covers both the request being in flight AND the held map
  // belonging to a different set of products. See stockFor above.
  const stockPending = stockStatus === "loading" || stockFor !== idsKey;

  const allRows = useMemo(
    () => buildInventoryRows(products, stock ?? undefined, locations),
    [products, stock, locations],
  );

  const stats = useMemo(() => summariseInventory(allRows), [allRows]);

  // ---- derived filter options ---------------------------------------------
  //
  // ⚠️ DERIVED FROM THE LOADED ROWS, NEVER RESTATED HERE. The hardcoded lists
  // this replaces named three invented warehouses and four brands -- the same
  // staleness that made "Poly" unfilterable on /catalog for exactly as long as
  // Poly was the only real product in the catalog.
  const brandOptions = useMemo(() => {
    const found = new Set<string>();
    for (const r of allRows) if (r.product.brand) found.add(r.product.brand);
    return Array.from(found).sort();
  }, [allRows]);

  const categoryOptions = useMemo(() => {
    const found = new Set<string>();
    for (const r of allRows) if (r.product.category) found.add(r.product.category);
    return Array.from(found).sort();
  }, [allRows]);

  const locationOptions = useMemo(() => {
    const found = new Set<string>();
    // A null location is deliberately NOT an option: it is the absence of a
    // place, not a place, and offering it would make "Not stated" filterable
    // as though it were a warehouse.
    for (const r of allRows) if (r.location) found.add(r.location);
    return Array.from(found).sort();
  }, [allRows]);

  // A select whose only option is "All" filters nothing and invites a click
  // that does nothing, so it is not rendered -- and its state is reset with it,
  // or a value chosen before a search would keep filtering from behind a
  // control no longer on screen.
  useEffect(() => {
    if (brandOptions.length === 0 && brand !== "All") setBrand("All");
  }, [brandOptions, brand]);
  useEffect(() => {
    if (categoryOptions.length === 0 && category !== "All") setCategory("All");
  }, [categoryOptions, category]);
  useEffect(() => {
    if (locationOptions.length === 0 && location !== "All") setLocation("All");
  }, [locationOptions, location]);

  const filtered = useMemo(() => {
    return allRows.filter((r) => {
      if (brand !== "All" && r.product.brand !== brand) return false;
      if (category !== "All" && r.product.category !== category) return false;
      if (location !== "All" && r.location !== location) return false;
      return true;
    });
  }, [allRows, brand, category, location]);

  // The levels behind the selected row's PRODUCT, so the panel shows the whole
  // breakdown rather than only the line that was clicked.
  const selectedLevels = useMemo(
    () => (selected ? allRows.filter((r) => r.product.id === selected.product.id) : []),
    [allRows, selected],
  );

  // A row that vanished should not stay open in the panel.
  useEffect(() => {
    setSelected((cur) => (cur && allRows.some((r) => r.key === cur.key) ? cur : null));
  }, [allRows]);

  const clearFilters = () => {
    setSearch(""); setLocation("All"); setBrand("All"); setCategory("All");
  };
  const filtersActive = search.length > 0 || location !== "All" || brand !== "All" || category !== "All";

  const tableHeight = typeof window !== "undefined"
    ? Math.max(layout.minTableHeight, window.innerHeight - layout.tableHeightOffset)
    : 480;

  // Success, and the provider had nothing to say. NOT an error, and worded so
  // it cannot be mistaken for one.
  const reportedNothing =
    catalogStatus === "ok" && stockStatus === "ok" && !stockPending && products.length > 0
    && stats.reported === 0 && stats.reportedNone === 0 && !noStockSource;
  const showNoStockSource = catalogStatus === "ok" && stockStatus === "ok" && noStockSource;

  return (
    <div
      data-testid="inventory-page"
      className="flex"
      style={{ height: "calc(100vh - 48px)" }}
    >
      <div className="flex flex-col" style={{ padding: spacing[24], flex: 1, minWidth: 0 }}>
        {/* HEADER */}
        <div className="flex items-start justify-between" style={{ marginBottom: spacing[16] }}>
          <div>
            {/* "Stock on Hand", not "Inventory": inventory implies a ledger --
                on hand, allocated, available, reorder point -- and this screen
                has one of those four. The SIDEBAR NAV LABEL is deliberately
                left alone; nav is muscle memory. */}
            <div
              data-testid="inventory-title"
              style={{
                fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.semibold,
                color: semanticColors.textPrimary, letterSpacing: typography.letterSpacing.tight,
              }}
            >
              Stock on Hand
            </div>
            <div
              data-testid="inventory-subtitle"
              style={{ fontSize: typography.fontSize.base, color: semanticColors.textSecondary, marginTop: spacing[4] }}
            >
              Quantities as reported per location. No allocations or commitments
              are known to this system, so these are not available-to-promise
              figures.
            </div>
          </div>
          {/* NO EXPORT BUTTON (app review P2-2, owner 2026-09-29): with no
              stock source it could only ever be a dead, disabled button; the
              export itself is listed for later (P3). A Stock Adjustment button
              is gone for good: an adjustment is a write to the system of
              record, and this app holds a read-only projection. */}
        </div>

        {/* SUMMARY STRIP -- WHAT IS KNOWN, IN THREE FIGURES.
            The fourth is not missing: total stock VALUE is gone with the
            invented unit cost behind it, along with the allocation and
            reorder-point counters. */}
        <div
          data-testid="inventory-summary-strip"
          className="flex items-center"
          style={{
            padding: spacing[16], marginBottom: spacing[16],
            backgroundColor: semanticColors.surface, border: `1px solid ${semanticColors.border}`,
            borderRadius: radius.base, boxShadow: shadows.sm, gap: spacing[32],
          }}
        >
          <StatBlock testid="stat-reported" value={stats.reported} label="Products reported with stock" />
          <StatBlock
            testid="stat-reported-none"
            value={stats.reportedNone}
            label="Reported as having none"
            color={stats.reportedNone > 0 ? colors.red600 : undefined}
          />
          {/* ⚠️ ITS OWN FIGURE, NEVER FOLDED INTO "0 IN STOCK". Nobody has
              looked at these; saying they have none would be a claim. */}
          <StatBlock
            testid="stat-unreported"
            value={stats.unreported}
            label="Not reported on"
            color={stats.unreported > 0 ? colors.amber600 : undefined}
          />
        </div>

        {/* FILTER BAR */}
        <div
          data-testid="inventory-filter-bar"
          className="flex items-center"
          style={{
            height: heights.filterBar, padding: `0 ${spacing[12]}px`, marginBottom: spacing[12],
            backgroundColor: semanticColors.panelBg,
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
            gap: spacing[12],
          }}
        >
          <div
            className="flex items-center"
            style={{
              flex: 1, minWidth: layout.searchMinWidthWide,
              height: heights.input, padding: `0 ${spacing[8]}px`,
              backgroundColor: semanticColors.surface,
              border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
              gap: spacing[6],
            }}
          >
            <Search size={iconSize.md} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />
            <input
              data-testid="inventory-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search part number or description"
              style={{
                flex: 1, minWidth: 0, height: "100%",
                fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
                background: "transparent", border: "none", outline: "none",
              }}
            />
          </div>

          {locationOptions.length > 0 && (
            <select data-testid="inventory-filter-location" value={location}
                    onChange={(e) => setLocation(e.target.value)} style={SELECT_STYLE}>
              <option value="All">Location: All</option>
              {locationOptions.map((l) => <option key={l} value={l}>{l}</option>)}
            </select>
          )}
          {brandOptions.length > 0 && (
            <select data-testid="inventory-filter-brand" value={brand}
                    onChange={(e) => setBrand(e.target.value)} style={SELECT_STYLE}>
              <option value="All">Brand: All</option>
              {brandOptions.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          )}
          {categoryOptions.length > 0 && (
            <select data-testid="inventory-filter-category" value={category}
                    onChange={(e) => setCategory(e.target.value)} style={SELECT_STYLE}>
              <option value="All">Category: All</option>
              {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <button
            data-testid="inventory-clear-filters"
            onClick={clearFilters}
            style={{
              background: "transparent", border: "none",
              color: filtersActive ? colors.indigo600 : semanticColors.textPlaceholder,
              fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
              cursor: filtersActive ? "pointer" : "default",
              padding: `0 ${spacing[4]}px`,
            }}
          >
            Clear
          </button>
        </div>

        {/* THE STOCK BANNER -- SOFT FAILURE, SAID PROMINENTLY.
            Not the picker's muted footnote: there, stock is decoration on a
            line being priced; here the quantity IS the screen's subject, so
            the reason must be as visible as the thing that failed. The rows
            below stay, because they are real catalog facts. */}
        {!!stockError && catalogStatus === "ok" && (
          <div
            data-testid="inventory-stock-error"
            className="flex items-center justify-between"
            style={{
              padding: `${spacing[10]}px ${spacing[12]}px`, marginBottom: spacing[12],
              backgroundColor: colors.amber50, border: `1px solid ${colors.amber200}`,
              borderRadius: radius.base, gap: spacing[12],
            }}
          >
            <span style={{ fontSize: typography.fontSize.sm, color: colors.amber600 }}>
              Stock levels could not be read, so every quantity below reads as not
              reported. The products themselves are current. {stockError}
            </span>
            <button
              data-testid="inventory-stock-retry"
              onClick={() => setStockRetry((n) => n + 1)}
              style={{
                height: heights.control, padding: `0 ${spacing[12]}px`, flexShrink: 0,
                backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                fontSize: typography.fontSize.base, cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* A SUCCESSFUL READ THAT REPORTED NOTHING. Not an error, and worded so
            it cannot be read as one -- "we asked and were told nothing" is a
            different fact from "we could not ask". */}
        {/* NO STOCK SOURCE CONNECTED (no-mock-stock-on-live). The backend
            serves no quantity at all until a real source exists. */}
        {showNoStockSource && (
          <div
            data-testid="inventory-no-stock-data"
            style={{
              padding: `${spacing[10]}px ${spacing[12]}px`, marginBottom: spacing[12],
              backgroundColor: semanticColors.panelBg, border: `1px solid ${semanticColors.border}`,
              borderRadius: radius.base,
              fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
            }}
          >
            {NO_STOCK_DATA_TEXT}. No stock source is connected to this system, so
            no quantity is shown for any product.
          </div>
        )}

        {reportedNothing && (
          <div
            data-testid="inventory-nothing-reported"
            style={{
              padding: `${spacing[10]}px ${spacing[12]}px`, marginBottom: spacing[12],
              backgroundColor: semanticColors.panelBg, border: `1px solid ${semanticColors.border}`,
              borderRadius: radius.base,
              fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
            }}
          >
            No stock has been reported for these products yet. The products are
            real; nothing has told this system what is on hand.
          </div>
        )}

        {catalogStatus === "error" ? (
          /* THE CATALOG FAILS LOUD. No rows, and NO TABLE -- an empty grid
             under a filter bar reads as "nothing matched", which is a
             different and wrong statement. */
          <div
            data-testid="inventory-catalog-error"
            style={{
              padding: spacing[24], textAlign: "center",
              backgroundColor: colors.red50, border: `1px solid ${colors.red100}`,
              borderRadius: radius.base,
            }}
          >
            <div style={{ fontSize: typography.fontSize.base, color: colors.red600, marginBottom: spacing[12] }}>
              {catalogError}
            </div>
            <button
              data-testid="inventory-catalog-retry"
              onClick={() => setCatalogRetry((n) => n + 1)}
              style={{
                height: heights.control, padding: `0 ${spacing[14]}px`,
                backgroundColor: colors.indigo600, color: semanticColors.onAccent,
                border: "none", borderRadius: radius.base,
                fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        ) : (
          <>
            {/* COUNT */}
            <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginBottom: spacing[8] }}>
              <span data-testid="inventory-filtered-count" className="tabular-nums">
                {filtered.length}
              </span>
              {filtered.length !== allRows.length ? (
                <> of <span className="tabular-nums">{allRows.length}</span> rows</>
              ) : (
                <> rows</>
              )}
              {/* ⚠️ SAID ONLY WHEN THE LIMIT ACTUALLY BIT, same as /catalog. */}
              {products.length === CATALOG_LIMIT && (
                <> — first {CATALOG_LIMIT} products, refine the search</>
              )}
            </div>

            <div style={{ flex: 1, minHeight: 0 }}>
              <InventoryTable
                rows={filtered}
                height={tableHeight}
                onRowClick={setSelected}
                selectedKey={selected?.key}
                stockPending={stockPending}
              />
            </div>
          </>
        )}
      </div>

      {selected && (
        <InventoryDetailPanel
          row={selected}
          levels={selectedLevels}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
